// Guild API service for fetching roster data
import { API_CONFIG, GUILD_REGION } from '../config/apiConfig';

const API_ENDPOINTS = {
  development: API_CONFIG.BASE_URL, // Use production API directly in development
  production: [
    API_CONFIG.BASE_URL, // Direct (primary)
    // CORS proxy fallback: if direct request fails (CORS/SSL), proxy request from same origin
    'https://corsproxy.io',
  ]
};

const API_KEY = API_CONFIG.API_KEY;

export interface GuildMember {
  character_id: number;
  name: string;
  level: number;
  rank: number;
  realm: string;
  last_updated: string;
  character_class: string;
  race: string;
  /** Present when returned by guild-members; used when character-profiles matches a different realm by name. */
  spec?: string;
}

export interface GuildApiResponse {
  success: boolean;
  count: number;
  data: GuildMember[];
}

// Rank mapping based on your guild hierarchy
// Leadership is displayed separately above rosters, so only show actual raiders
// Note: API rank numbers are offset from in-game ranks, so shifting up by 2 total
export const RANK_MAPPING = {
  0: { name: 'Guild Master', team: 'excluded', priority: 1, show: false }, // Leadership - excluded
  1: { name: 'Officer', team: 'excluded', priority: 2, show: false }, // Leadership - excluded
  2: { name: 'Officer Alt', team: 'excluded', priority: 3, show: false }, // Leadership - excluded
  3: { name: 'Member', team: 'excluded', priority: 6, show: false },
  4: { name: 'Mythic Raider', team: 'mythic', priority: 4, show: true }, // API rank 4 = Mythic Raider
  5: { name: 'Heroic Raider', team: 'heroic', priority: 5, show: true }, // API rank 5 = Heroic Raider
  6: { name: 'Member', team: 'excluded', priority: 6, show: false },
  7: { name: 'Member', team: 'excluded', priority: 6, show: false },
  8: { name: 'Member', team: 'excluded', priority: 6, show: false },
} as const;

export type TeamType = 'mythic' | 'heroic' | 'excluded';

export interface RankInfo {
  name: string;
  team: TeamType;
  priority: number;
  show: boolean;
}

/**
 * Try multiple API endpoints and return the first successful response
 */
async function tryApiEndpoints(): Promise<Response> {
  // Always use production endpoints since they work and have proper CORS
  const endpoints = API_ENDPOINTS.production;

  let lastError: Error | null = null;

  for (const endpoint of endpoints) {
    try {
      // Handle different proxy formats
      let url: string;
      let headers: Record<string, string> = {
        'X-API-Key': API_KEY,
        'Content-Type': 'application/json',
      };
      
      if (endpoint.includes('corsproxy.io')) {
        // corsproxy.io format: ?<encoded_target_url> (proxy fetches target; API key in target URL)
        const targetUrl = `${API_CONFIG.BASE_URL}/guild-members?limit=1000&api_key=${API_KEY}`;
        url = `${endpoint}/?${encodeURIComponent(targetUrl)}`;
        delete headers['X-API-Key'];
      } else if (endpoint.includes('api.codetabs.com')) {
        // api.codetabs.com format - add API key as URL parameter
        url = `${endpoint}/guild-members?limit=1000&api_key=${API_KEY}`;
        // Remove API key header for api.codetabs.com as it doesn't support custom headers
        delete headers['X-API-Key'];
      } else {
        // Direct API - include API key in both header and URL for redundancy
        url = `${endpoint}/guild-members?limit=1000&api_key=${API_KEY}`;
      }
      
      const response = await fetch(url, {
        method: 'GET',
        headers,
        mode: 'cors',
        // Increased timeout to 60 seconds to give the server more time to respond
        signal: AbortSignal.timeout(60000), // 60 second timeout
      });

      if (response.ok) {
        return response;
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      const errorName = error instanceof Error ? error.constructor.name : 'Unknown';
      
      // Log details to console; throw generic message for UI
      if (errorName === 'TimeoutError' || errorMessage.includes('signal timed out') || errorMessage.includes('timeout')) {
        console.error(`Timeout Error for ${endpoint}: The API server did not respond within 60 seconds.`);
        lastError = new Error('API connection failed');
      } else if (errorMessage.includes('CERT') || errorMessage.includes('certificate') || errorMessage.includes('ERR_CERT') || errorMessage.includes('ERR_CERT_DATE_INVALID')) {
        console.error(`SSL Certificate Error for ${endpoint}: The server's SSL certificate has expired or is invalid.`);
        lastError = new Error('API connection failed');
      } else if (errorMessage.includes('Failed to fetch') || errorMessage.includes('NetworkError') || errorName === 'TypeError') {
        console.error(`Network Error for ${endpoint}: Unable to connect. May be SSL/certificate, CORS, DNS, or server down.`, {
          error: errorMessage,
          type: errorName,
          endpoint,
          fullError: error
        });
        console.info('Tip: Open the API URL in a new tab to check SSL. In Network tab look for (blocked) or CORS errors.');
        lastError = new Error('API connection failed');
      } else {
        console.warn(`Failed to connect to ${endpoint}:`, {
          error: errorMessage,
          type: errorName,
          endpoint,
          fullError: error
        });
        lastError = new Error('API connection failed');
      }
    }
  }

  throw lastError || new Error('API connection failed');
}

/**
 * Fetch all guild members from the API
 */
export async function fetchGuildMembers(): Promise<GuildMember[]> {
  try {
    const response = await tryApiEndpoints();

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status} - ${response.statusText}`);
    }

    // Check if response is actually JSON
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      throw new Error('API returned non-JSON response - likely a CORS or routing issue');
    }

    const data: GuildApiResponse = await response.json();
    
    if (!data.success) {
      throw new Error('API returned success: false');
    }

    return data.data;
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    console.error('Failed to fetch guild members:', error);
    console.error('Error details:', {
      message: errorMessage,
      stack: error instanceof Error ? error.stack : undefined,
    });
    // Re-throw with generic message for UI (details stay in console)
    if (errorMessage === 'API connection failed') {
      throw error;
    }
    throw new Error('API connection failed');
  }
}

/**
 * Search for a specific guild member by name
 */
export async function searchGuildMember(characterName: string): Promise<GuildMember | null> {
  try {
    // Always use production endpoints since they work and have proper CORS
    const endpoints = API_ENDPOINTS.production;

    for (const endpoint of endpoints) {
      try {
        // Handle different proxy formats
        let url: string;
        let headers: Record<string, string> = {
          'X-API-Key': API_KEY,
        };
        
        if (endpoint.includes('corsproxy.io')) {
          // corsproxy.io format: ?<encoded_target_url>
          const targetUrl = `${API_CONFIG.BASE_URL}/guild-members?character_name=${encodeURIComponent(characterName)}&api_key=${API_KEY}`;
          url = `${endpoint}/?${encodeURIComponent(targetUrl)}`;
          delete headers['X-API-Key'];
        } else if (endpoint.includes('api.codetabs.com')) {
          // api.codetabs.com format - add API key as URL parameter
          url = `${endpoint}/guild-members?character_name=${encodeURIComponent(characterName)}&api_key=${API_KEY}`;
          // Remove API key header for api.codetabs.com as it doesn't support custom headers
          delete headers['X-API-Key'];
        } else {
          // Direct API - include API key in both header and URL for redundancy
          url = `${endpoint}/guild-members?character_name=${encodeURIComponent(characterName)}&api_key=${API_KEY}`;
        }

        const response = await fetch(url, {
          headers,
          method: 'GET',
          mode: 'cors',
          signal: AbortSignal.timeout(60000), // 60 second timeout
        });

        if (response.ok) {
          const data: GuildApiResponse = await response.json();
          
          if (!data.success || data.count === 0) {
            return null;
          }

          return data.data[0];
        }
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        const errorName = error instanceof Error ? error.constructor.name : 'Unknown';
        
        // Log timeout errors specifically
        if (errorName === 'TimeoutError' || errorMessage.includes('signal timed out') || errorMessage.includes('timeout')) {
          console.warn(`Search timeout on ${endpoint}: The API server did not respond within 60 seconds.`);
        } else {
          console.warn(`Search failed on ${endpoint}:`, error);
        }
        continue; // Try next endpoint
      }
    }

    return null; // All endpoints failed
  } catch (error) {
    console.error('Failed to search guild member:', error);
    return null;
  }
}

/**
 * Get rank information for a guild member
 */
export function getRankInfo(rank: number): RankInfo {
  const mapping = RANK_MAPPING[rank as keyof typeof RANK_MAPPING];
  if (mapping) {
    return mapping as RankInfo;
  }
  return RANK_MAPPING[8] as RankInfo;
}

/**
 * Filter guild members for main rosters (level 80, mythic/heroic raiders only)
 */
export function filterForMainRosters(members: GuildMember[]): GuildMember[] {
  const filteredMembers = members.filter(member => {
    // Exclude specific characters (e.g., removed from roster but still in API)
    const excludedCharacters = ['rentwu', 'sigyr', 'joniquaza', 'Jadedxoxo', 'Tâsât', 'Extolled', 'Lesliewar'];
    const excludedLower = excludedCharacters.map(c => c.toLowerCase());
    if (excludedLower.includes(member.name.toLowerCase())) {
      return false;
    }
    
    // Only level 80 members
    if (member.level < 80) {
      return false;
    }
    
    // Only show mythic raiders (rank 4) and heroic raiders (rank 5)
    if (member.rank !== 4 && member.rank !== 5) {
      return false;
    }
    
    return true;
  });
  return filteredMembers;
}

/**
 * Categorize filtered guild members by team (mythic/heroic) based on rank
 */
export function categorizeByTeam(members: GuildMember[]): { mythic: GuildMember[]; heroic: GuildMember[] } {
  // First filter for main roster members
  const filteredMembers = filterForMainRosters(members);
  
  const mythic: GuildMember[] = [];
  const heroic: GuildMember[] = [];

  filteredMembers.forEach(member => {
    if (member.rank === 4) {
      mythic.push(member);
    } else if (member.rank === 5) {
      heroic.push(member);
    }
  });

  // Sort by name for consistent display
  mythic.sort((a, b) => a.name.localeCompare(b.name));
  heroic.sort((a, b) => a.name.localeCompare(b.name));
  return { mythic, heroic };
}

/**
 * Convert API member to roster format compatible with existing UI
 */
export function convertToRosterFormat(member: GuildMember): any {
  const rankInfo = getRankInfo(member.rank);
  
  return {
    id: member.character_id.toString(),
    name: member.name,
    level: member.level,
    rank: rankInfo.name,
    rankNumber: member.rank,
    realm: member.realm,
    region: GUILD_REGION,
    team: rankInfo.team,
    last_updated: member.last_updated,
    // spec/class from guild-members when present; private API can refine when realm matches
    class: member.character_class || '',
    spec: member.spec || '',
    character_class: member.character_class || '',
    race: member.race || '',
    // Mythic+ tracking properties (will be populated from Firebase)
    mythicPlusThisWeek: 0,
    weeksMetRequirement: 0,
  };
}
